import pathfinder from '@fiverr-private/pathfinder';
import { translate } from '@fiverr-private/i18n-react';
import { LOGO_MAKER_SOURCE } from '../../../../constants';
import { type Link } from '../../../shared/getContentLinks';
import { type BigQueryEventName } from '../../../events/bigQuery';

const T_PREFIX = 'header.logo_maker';

const bqEvent = (name: string): BigQueryEventName => `clicked_header.${name}` as BigQueryEventName;

const createLogoPath = (tag: string, type: string) => pathfinder('logo_maker_buyer_ideas_page', { tag, type });

const createCategoryPath = (id: string) =>
    pathfinder(
        'short_category_sub_category',
        { category_id: 'graphics-design', id },
        { query: { source: LOGO_MAKER_SOURCE } }
    );

export interface LogoMakerExploreLinks {
    popularLogosLinks: Link[];
    solutionsLinks: Link[];
    learnDiscoverLinks: Link[];
}

export const getLogoMakerContentLinks = (): LogoMakerExploreLinks => {
    const popularLogosLinks: Link[] = [
        {
            url: createLogoPath('gaming', 'industry'),
            label: translate(`${T_PREFIX}.gaming_logos`),
            bigQueryEventName: bqEvent('gaming'),
        },
        {
            url: createLogoPath('sports', 'tag'),
            label: translate(`${T_PREFIX}.sports_logos`),
            bigQueryEventName: bqEvent('sports'),
        },
        {
            url: createLogoPath('restaurants', 'industry'),
            label: translate(`${T_PREFIX}.restaurant_logos`),
            bigQueryEventName: bqEvent('restaurant'),
        },
        {
            url: createLogoPath('food', 'tag'),
            label: translate(`${T_PREFIX}.food_logos`),
            bigQueryEventName: bqEvent('food and drinks'),
        },
        {
            url: createLogoPath('real-estate', 'industry'),
            label: translate(`${T_PREFIX}.real_estate_logos`),
            bigQueryEventName: bqEvent('real estate'),
        },
        {
            url: createLogoPath('fashion-and-apparel', 'industry'),
            label: translate(`${T_PREFIX}.fashion_logos`),
            bigQueryEventName: bqEvent('fashion'),
        },
        {
            url: createLogoPath('law', 'industry'),
            label: translate(`${T_PREFIX}.law_logos`),
            bigQueryEventName: bqEvent('law'),
        },
        {
            url: createLogoPath('music', 'industry'),
            label: translate(`${T_PREFIX}.music_logos`),
            bigQueryEventName: bqEvent('music'),
        },
        {
            url: createLogoPath('medical', 'tag'),
            label: translate(`${T_PREFIX}.medical_logos`),
            bigQueryEventName: bqEvent('medical'),
        },
        {
            url: createLogoPath('youtube', 'tag'),
            label: translate(`${T_PREFIX}.youtube_logos`),
            bigQueryEventName: bqEvent('youtube'),
        },
        {
            url: createLogoPath('clothing', 'tag'),
            label: translate(`${T_PREFIX}.clothing_logos`),
            bigQueryEventName: bqEvent('clothing'),
        },
    ];

    const solutionsLinks: Link[] = [
        {
            url: createCategoryPath('creative-logo-design'),
            label: translate(`${T_PREFIX}.hire_a_designer`),
            bigQueryEventName: bqEvent('hire a designer'),
        },
        {
            url: pathfinder('logo_maker_brand_solutions_page'),
            label: translate(`${T_PREFIX}.branding_hub`),
            bigQueryEventName: bqEvent('branding hub'),
        },
        {
            url: createCategoryPath('sample-business-cards-design'),
            label: translate(`${T_PREFIX}.business_cards_design`),
            bigQueryEventName: bqEvent('business cards design'),
        },
        {
            url: createCategoryPath('social-media-design'),
            label: translate(`${T_PREFIX}.social_media_design`),
            bigQueryEventName: bqEvent('social media design'),
        },
        {
            url: createCategoryPath('email-design'),
            label: translate(`${T_PREFIX}.email_design`),
            bigQueryEventName: bqEvent('email design'),
        },
        {
            url: createCategoryPath('website-design'),
            label: translate(`${T_PREFIX}.website_design`),
            bigQueryEventName: bqEvent('website design'),
        },
        {
            url: createCategoryPath('t-shirts'),
            label: translate(`${T_PREFIX}.apparel_and_merchandise_design`),
            bigQueryEventName: bqEvent('apparel and merchandise design'),
        },
        {
            url: pathfinder('gig', { id: 'promotional-design' }, { query: { source: LOGO_MAKER_SOURCE } }),
            label: translate(`${T_PREFIX}.promotional_design`),
            bigQueryEventName: bqEvent('promotional design'),
        },
        {
            url: pathfinder('homepage', {}, { query: { source: LOGO_MAKER_SOURCE } }),
            label: translate(`${T_PREFIX}.fiverr_freelance_services_marketplace`),
            bigQueryEventName: bqEvent('fiverr freelance services marketplace'),
        },
    ];

    const learnDiscoverLinks: Link[] = [
        {
            url: pathfinder('logo_maker_about_page'),
            label: translate(`${T_PREFIX}.about_us`),
            bigQueryEventName: bqEvent('about us'),
        },
        {
            url: pathfinder('logo_maker_meet_the_designers'),
            label: translate(`${T_PREFIX}.meet_the_designers`),
            bigQueryEventName: bqEvent('meet the designers'),
        },
        {
            url: pathfinder('logo_maker_how_logo_maker_works_page'),
            label: translate(`${T_PREFIX}.how_it_works`),
            bigQueryEventName: bqEvent('how it works'),
        },
        {
            url: pathfinder('fiverr_guides_home'),
            label: translate(`${T_PREFIX}.design_branding_guides`),
            bigQueryEventName: bqEvent('design & branding guides'),
        },
        {
            url: pathfinder('logo_maker_discover_logo_ideas_page'),
            label: translate(`${T_PREFIX}.logo_ideas_inspirations`),
            bigQueryEventName: bqEvent('logo ideas & inspirations'),
        },
        {
            url: pathfinder('support_get_help_page', {}, { query: { source: LOGO_MAKER_SOURCE } }),
            label: translate(`${T_PREFIX}.support`),
            bigQueryEventName: bqEvent('support'),
        },
    ];

    return { popularLogosLinks, solutionsLinks, learnDiscoverLinks };
};
